import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useBlocker, useLocation } from 'react-router';
import { ConfirmDialog } from '@/components/Dialog/ConfirmDialog';
import { Callout } from '@/components/Elements/Callout';
import { Link } from '@/components/Elements/Link';
import { BILLING_PORTAL_URL } from '@/config';
import type { AuthUser } from '@/features/auth/types';
import { useUser, useUserPermission } from '@/lib/auth';
import {
  type ProfileSettingsOverlayState,
  useProfileSettingsOverlayNavigation,
} from '../lib/profileSettingsOverlay';
import {
  getOptionalProfileIssues,
  hasNameQualityIssue,
} from '../lib/profileQuality';
import { ProfileSection } from './ProfileSection';
import { ProfileSettingsDialog } from './ProfileSettingsDialog';
import { UserProfileSettingsForm } from './UserProfileSettingsForm';

const SAVED_NOTICE_DURATION_MS = 3000;

type ProfileSettingsDialogSessionProps = {
  user: AuthUser;
  overlay: ProfileSettingsOverlayState;
  currentPlans: string[];
  isOnlyBasicLicense: boolean;
  closeProfileSettings: () => void;
  onSaved: () => void;
};

const ProfileSettingsDialogSession = ({
  user,
  overlay,
  currentPlans,
  isOnlyBasicLicense,
  closeProfileSettings,
  onSaved,
}: ProfileSettingsDialogSessionProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [discardPromptOpen, setDiscardPromptOpen] = useState(false);
  const allowNextNavigationRef = useRef(false);
  const previousLocationKeyRef = useRef(location.key);
  const blocker = useBlocker(() =>
    Boolean(hasUnsavedChanges && !allowNextNavigationRef.current)
  );
  const subscriptionSearch = createSearchParams({
    display_basic: 'false',
  }).toString();
  const showDiscardPrompt =
    discardPromptOpen || (blocker.state === 'blocked' && !isSaving);
  const nameNeedsAttention = hasNameQualityIssue(user.profileQualityIssues);
  const hasOptionalProfileIssues =
    getOptionalProfileIssues(user.profileQualityIssues).length > 0;
  const displayedPlans =
    currentPlans.length > 1
      ? currentPlans.filter((plan) => plan !== 'Basic')
      : currentPlans;

  useEffect(() => {
    if (blocker.state === 'blocked' && isSaving) blocker.reset();
  }, [blocker, isSaving]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const preventHardNavigation = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', preventHardNavigation);
    return () =>
      window.removeEventListener('beforeunload', preventHardNavigation);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (previousLocationKeyRef.current === location.key) return;

    previousLocationKeyRef.current = location.key;
    allowNextNavigationRef.current = false;
  }, [location.key]);

  const requestClose = () => {
    if (isSaving) return;
    if (hasUnsavedChanges) {
      setDiscardPromptOpen(true);
      return;
    }
    closeProfileSettings();
  };

  const cancelDiscard = () => {
    setDiscardPromptOpen(false);
    if (blocker.state === 'blocked') blocker.reset();
  };

  const confirmDiscard = () => {
    allowNextNavigationRef.current = true;
    setDiscardPromptOpen(false);
    if (blocker.state === 'blocked') blocker.proceed();
    else closeProfileSettings();
  };

  const handleSaved = () => {
    allowNextNavigationRef.current = true;
    closeProfileSettings();
    onSaved();
  };

  return (
    <>
      <ProfileSettingsDialog isOpen onClose={requestClose} isSaving={isSaving}>
        <UserProfileSettingsForm
          key={user.id}
          user={user}
          profileNotice={
            overlay.emailChangeSuccess ||
            nameNeedsAttention ||
            hasOptionalProfileIssues ? (
              <div className="mb-4 space-y-2">
                {overlay.emailChangeSuccess && (
                  <Callout variant="success" size="compact">
                    {t('メールアドレス変更が完了しました。')}
                  </Callout>
                )}
                {nameNeedsAttention && (
                  <Callout variant="warning" size="compact">
                    {t(
                      '現在の氏名は未設定、または一時的に設定された可能性があります。正しい氏名を入力して保存してください。'
                    )}
                  </Callout>
                )}
                {hasOptionalProfileIssues && (
                  <Callout variant="info" size="compact">
                    {t(
                      '表示言語やプロフィール画像は、必要に応じて設定できます。'
                    )}
                  </Callout>
                )}
              </div>
            ) : null
          }
          contractSection={
            <ProfileSection title={t('契約状況')}>
              <div className="space-y-2 text-sm text-gray-700">
                <div>
                  <div className="mb-2 font-medium text-gray-900">
                    {t('契約中のプラン')}
                  </div>
                  {displayedPlans.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {displayedPlans.map((plan) => (
                        <span
                          key={plan}
                          data-plan-badge
                          className="inline-flex items-center rounded-full border border-primary-teal/30 bg-primary-teal/5 px-3 py-1 text-xs font-semibold text-primary-teal"
                        >
                          {plan}
                        </span>
                      ))}
                    </div>
                  ) : (
                    '-'
                  )}
                </div>
                {user.hasPaymentCustomer && (
                  <Link to={BILLING_PORTAL_URL} target="_blank">
                    {t('請求情報の管理')}
                  </Link>
                )}
                {isOnlyBasicLicense && (
                  <div className="rounded-lg border border-primary-teal/30 bg-primary-teal/5 p-3">
                    <div className="font-medium text-gray-900">
                      {t('さらに多くの機能を利用できます')}
                    </div>
                    <Link
                      className="mt-1 inline-flex font-medium"
                      to={{
                        pathname: '/subscription',
                        search: `?${subscriptionSearch}`,
                      }}
                      target="_self"
                    >
                      {t('Plusプランにアップグレード')}
                    </Link>
                    <p className="mt-2 text-gray-600">
                      {t(
                        '組織の契約プランを利用する場合は、所属する組織の管理者へご連絡ください。'
                      )}
                    </p>
                  </div>
                )}
              </div>
            </ProfileSection>
          }
          onUnsavedChangesChange={setHasUnsavedChanges}
          onSavingChange={setIsSaving}
          onSaved={handleSaved}
          showSavedMessage={false}
        />
      </ProfileSettingsDialog>

      <ConfirmDialog
        isOpen={showDiscardPrompt}
        onClose={cancelDiscard}
        onConfirm={confirmDiscard}
        title={t('変更を破棄しますか？')}
        messages={[t('保存されていない変更内容は失われます。')]}
        confirmText={t('変更を破棄')}
        cancelText={t('編集を続ける')}
        confirmColor="danger"
      />
    </>
  );
};

export const ProfileSettingsDialogHost = () => {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const { licenseLabels: currentPlans, isOnlyBasicLicense } =
    useUserPermission();
  const { overlay, closeProfileSettings } =
    useProfileSettingsOverlayNavigation();
  const [savedNoticeVersion, setSavedNoticeVersion] = useState(0);

  useEffect(() => {
    if (savedNoticeVersion === 0) return;

    const timeout = window.setTimeout(
      () => setSavedNoticeVersion(0),
      SAVED_NOTICE_DURATION_MS
    );
    return () => window.clearTimeout(timeout);
  }, [savedNoticeVersion]);

  return (
    <>
      {savedNoticeVersion > 0 && (
        <Callout
          variant="success"
          size="compact"
          className="fixed right-4 top-4 z-[80] max-w-sm shadow-lg"
        >
          {t('設定を保存しました')}
        </Callout>
      )}

      {overlay && user && (
        <ProfileSettingsDialogSession
          key={`${user.id}:${overlay.entryMode}:${overlay.emailChangeSuccess}`}
          user={user}
          overlay={overlay}
          currentPlans={currentPlans}
          isOnlyBasicLicense={isOnlyBasicLicense}
          closeProfileSettings={closeProfileSettings}
          onSaved={() => setSavedNoticeVersion((version) => version + 1)}
        />
      )}
    </>
  );
};
